/**
 * Mock interactive producer screen for quiz questions.
 * Matches the real producer page layout exactly.
 */

import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/Button";

export interface MockProducerRider {
  id: string;
  name: string;
  nickname?: string;
  attempts: number;
  currentAttempt: number;
  judgeScores?: Record<string, Record<number, number | null>>;
  finals?: Record<number, number | null>;
}

export interface MockProducerHeat {
  id: string;
  name: string;
  division?: string;
  isLive?: boolean;
}

export interface ProducerLiveState {
  isLive: boolean;
  liveHeatId: string | null;
  focusRiderId: string | null;
  scoresRiderId: string | null;
  showLeaderboard: boolean;
  showHeatScores: boolean;
  showRiderScores: boolean;
  showCurrentHeat: boolean;
  overlayStyle: string;
}

interface MockProducerScreenProps {
  heats: MockProducerHeat[];
  riders?: MockProducerRider[];
  ridersByHeat?: Record<string, MockProducerRider[]>;
  /** All available riders for the Edit Heats modal rider picker */
  availableRiders?: { id: string; name: string; nickname?: string }[];
  initialState: ProducerLiveState;
  onStateChange?: (state: ProducerLiveState, action: string) => void;
  onAttemptChange?: (heatId: string, riderId: string, newAttempt: number, direction: "advance" | "revert") => void;
  onHeatSelect?: (heatId: string) => void;
  /** Called when riders are added/removed from a heat via Edit Heats */
  onRidersChanged?: (heatId: string, riderIds: string[]) => void;
}

const STYLES = ["Steel", "Olympic", "Broadcast", "Neon", "Clean"];

export function MockProducerScreen({
  heats,
  riders: flatRiders,
  ridersByHeat,
  availableRiders = [],
  initialState,
  onStateChange,
  onAttemptChange,
  onHeatSelect,
  onRidersChanged,
}: MockProducerScreenProps) {
  const getInitialRiders = (heatId: string) => ridersByHeat?.[heatId] ?? flatRiders ?? [];
  const [state, setState] = useState<ProducerLiveState>(initialState);
  const [allRiders, setAllRiders] = useState<Record<string, MockProducerRider[]>>(() => {
    const map: Record<string, MockProducerRider[]> = {};
    for (const h of heats) map[h.id] = getInitialRiders(h.id);
    return map;
  });
  const [selectedHeat, setSelectedHeat] = useState(initialState.liveHeatId ?? heats[0]?.id ?? "");
  const [editHeatId, setEditHeatId] = useState<string | null>(null);

  useEffect(() => {
    if (ridersByHeat) setAllRiders(ridersByHeat);
  }, [ridersByHeat]);

  const riders = allRiders[selectedHeat] ?? [];

  const updateState = useCallback((patch: Partial<ProducerLiveState>, action: string) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      onStateChange?.(next, action);
      return next;
    });
  }, [onStateChange]);

  const goLive = useCallback((heatId: string) => {
    updateState({ isLive: true, liveHeatId: heatId, focusRiderId: null, scoresRiderId: null }, "go-live");
  }, [updateState]);

  const goDark = useCallback(() => {
    updateState({ isLive: false, focusRiderId: null, scoresRiderId: null, showLeaderboard: false, showHeatScores: false, showRiderScores: false }, "go-dark");
  }, [updateState]);

  const advanceAttempt = useCallback((riderId: string) => {
    setAllRiders((prev) => {
      const hr = prev[selectedHeat] ?? [];
      const r = hr.find((x) => x.id === riderId);
      if (!r || r.currentAttempt >= r.attempts) return prev;
      const n = r.currentAttempt + 1;
      onAttemptChange?.(selectedHeat, riderId, n, "advance");
      return { ...prev, [selectedHeat]: hr.map((x) => x.id === riderId ? { ...x, currentAttempt: n } : x) };
    });
  }, [selectedHeat, onAttemptChange]);

  const revertAttempt = useCallback((riderId: string) => {
    setAllRiders((prev) => {
      const hr = prev[selectedHeat] ?? [];
      const r = hr.find((x) => x.id === riderId);
      if (!r || r.currentAttempt <= 0) return prev;
      const n = r.currentAttempt - 1;
      onAttemptChange?.(selectedHeat, riderId, n, "revert");
      return { ...prev, [selectedHeat]: hr.map((x) => x.id === riderId ? { ...x, currentAttempt: n } : x) };
    });
  }, [selectedHeat, onAttemptChange]);

  const handleHeatChange = useCallback((heatId: string) => {
    setSelectedHeat(heatId);
    if (state.isLive) updateState({ liveHeatId: heatId }, "switch-heat");
    onHeatSelect?.(heatId);
  }, [state.isLive, updateState, onHeatSelect]);

  // Group heats for optgroup
  const divisionMap = new Map<string | undefined, MockProducerHeat[]>();
  for (const h of heats) {
    const key = h.division;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(h);
  }

  return (
    <div style={{ padding: "0 4px" }}>
      {/* ── Header row: LIVE badge + heat dropdown + Edit Heats + Go Live/Dark ── */}
      <div className="flex gap-3 items-center" style={{ padding: "12px 0 16px", borderBottom: "1px solid var(--c-border)", marginBottom: 20 }}>
        {state.isLive ? (
          <span className="badge badge-live">LIVE</span>
        ) : (
          <span className="badge badge-off">OFF AIR</span>
        )}
        <select
          value={selectedHeat}
          onChange={(e) => handleHeatChange(e.target.value)}
          style={{
            minWidth: 180, fontFamily: "var(--font-sans)", fontSize: 14,
            padding: "6px 12px", background: "var(--c-surface)",
            border: "1px solid var(--c-border)", borderRadius: 6,
            color: "var(--c-text)", cursor: "pointer",
          }}
        >
          {Array.from(divisionMap.entries()).map(([div, divHeats]) =>
            div ? (
              <optgroup key={div} label={div}>
                {divHeats.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </optgroup>
            ) : (
              divHeats.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)
            ),
          )}
        </select>
        <Button variant="secondary" size="sm" onClick={() => setEditHeatId(selectedHeat)}>Edit Heats</Button>
        {state.isLive ? (
          <Button variant="danger" size="sm" onClick={goDark}>Go Dark</Button>
        ) : (
          <Button size="sm" onClick={() => goLive(selectedHeat)} disabled={!selectedHeat}>Go Live</Button>
        )}
      </div>

      {/* ── Overlay controls ── */}
      <div className="flex gap-3" style={{ marginBottom: 20, flexWrap: "wrap" }}>
        <Button
          variant={state.showCurrentHeat ? "primary" : "secondary"} size="sm"
          onClick={() => updateState({ showCurrentHeat: !state.showCurrentHeat }, state.showCurrentHeat ? "hide-heat-name" : "show-heat-name")}
        >
          {state.showCurrentHeat ? "Hide Heat Name" : "Show Heat Name"}
        </Button>
        <Button
          variant={state.showLeaderboard ? "primary" : "secondary"} size="sm"
          onClick={() => updateState({ showLeaderboard: !state.showLeaderboard }, state.showLeaderboard ? "hide-leaderboard" : "show-leaderboard")}
        >
          {state.showLeaderboard ? "Hide Heat Leaderboard" : "Show Heat Leaderboard"}
        </Button>
        <Button
          variant={state.showHeatScores ? "primary" : "secondary"} size="sm"
          onClick={() => updateState({ showHeatScores: !state.showHeatScores }, state.showHeatScores ? "hide-heat-scores" : "show-heat-scores")}
        >
          {state.showHeatScores ? "Hide Heat Riders" : "Show Heat Riders"}
        </Button>
        <Button
          variant={state.showRiderScores ? "primary" : "secondary"} size="sm"
          onClick={() => updateState({ showRiderScores: !state.showRiderScores }, state.showRiderScores ? "hide-rider-scores" : "show-rider-scores")}
        >
          {state.showRiderScores ? "Hide Rider Scores" : "Show Rider Scores"}
        </Button>
        <Button variant="secondary" size="sm"
          onClick={() => updateState({ focusRiderId: null, scoresRiderId: null }, "clear-focus")}
        >
          Clear Focus
        </Button>
      </div>

      {/* ── Overlay style ── */}
      <div style={{ marginBottom: 20 }}>
        <div className="field-label" style={{ marginBottom: 8 }}>Overlay Style</div>
        <div className="flex gap-2">
          {STYLES.map((s) => {
            const id = s.toLowerCase();
            return (
              <button key={id}
                className={`btn btn-sm ${state.overlayStyle === id ? "btn-primary" : "btn-secondary"}`}
                onClick={() => updateState({ overlayStyle: id }, `style-${id}`)}
              >{s}</button>
            );
          })}
        </div>
      </div>

      {/* ── Rider control cards ── */}
      <div className="control-grid">
        {riders.map((r) => {
          const isFocused = state.focusRiderId === r.id;
          const isScoresShown = state.scoresRiderId === r.id;
          const maxAttempts = r.attempts;

          // Find best attempt
          let bestAttempt = -1;
          let bestVal = -1;
          if (r.finals) {
            for (let a = 1; a <= maxAttempts; a++) {
              const v = r.finals[a];
              if (v != null && v > bestVal) { bestVal = v; bestAttempt = a; }
            }
          }

          return (
            <div key={r.id} className={`rider-control-card ${isFocused ? "focused" : ""}`}>
              <div className="rider-name">{r.name}</div>
              {r.nickname && (
                <div className="rider-meta"><span>{r.nickname}</span></div>
              )}

              {/* Attempt score boxes */}
              {r.finals && maxAttempts > 0 && (
                <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                  {Array.from({ length: maxAttempts }, (_, i) => {
                    const a = i + 1;
                    const score = r.finals?.[a];
                    const isBest = a === bestAttempt;
                    return (
                      <div key={a} style={{
                        padding: "4px 8px", fontSize: 12, fontWeight: 700,
                        fontFamily: "var(--font-mono)",
                        background: isBest ? "var(--c-accent)" : "var(--c-surface-2)",
                        color: isBest ? "#fff" : score != null ? "var(--c-text)" : "var(--c-text-dim)",
                        border: `1px solid ${isBest ? "var(--c-accent)" : "var(--c-border)"}`,
                        minWidth: 36, textAlign: "center",
                      }}>
                        {score != null ? score.toFixed(1) : "-"}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Attempt dots + controls */}
              {maxAttempts > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px", fontSize: 11 }}
                    onClick={() => revertAttempt(r.id)} disabled={r.currentAttempt <= 0}>-</button>
                  <div style={{ display: "flex", gap: 3 }}>
                    {Array.from({ length: maxAttempts }, (_, i) => {
                      const done = i + 1 <= r.currentAttempt;
                      return <div key={i} style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: done ? "var(--c-accent)" : "transparent",
                        border: `1px solid ${done ? "var(--c-accent)" : "var(--c-border)"}`,
                      }} />;
                    })}
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px", fontSize: 11 }}
                    onClick={() => advanceAttempt(r.id)} disabled={r.currentAttempt >= maxAttempts}>+</button>
                  <span className="text-mono text-muted" style={{ fontSize: 10 }}>{r.currentAttempt}/{maxAttempts}</span>
                </div>
              )}

              {/* Focus / Show Scores */}
              <div className="rider-actions">
                <Button variant={isFocused ? "primary" : "ghost"} size="sm"
                  onClick={() => updateState({ focusRiderId: isFocused ? null : r.id }, isFocused ? "unfocus" : `focus-${r.id}`)}>
                  {isFocused ? "Unfocus" : "Focus"}
                </Button>
                <Button variant={isScoresShown ? "primary" : "ghost"} size="sm"
                  onClick={() => updateState({ scoresRiderId: isScoresShown ? null : r.id }, isScoresShown ? "hide-scores" : `scores-${r.id}`)}>
                  {isScoresShown ? "Hide Scores" : "Show Scores"}
                </Button>
              </div>

              {/* Score table */}
              {r.judgeScores && Object.keys(r.judgeScores).length > 0 && (
                <div style={{ marginTop: 8, borderTop: "1px solid var(--c-border)", paddingTop: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: "var(--font-mono)" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", color: "var(--c-text-dim)", fontWeight: 400, padding: "2px 4px" }} />
                        {Array.from({ length: maxAttempts }, (_, i) => (
                          <th key={i} style={{ textAlign: "center", color: "var(--c-text-dim)", fontWeight: 400, padding: "2px 4px" }}>A{i + 1}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(r.judgeScores).map(([judge, attempts]) => (
                        <tr key={judge}>
                          <td style={{ padding: "2px 4px", color: "var(--c-text-muted)" }}>{judge}</td>
                          {Array.from({ length: maxAttempts }, (_, i) => {
                            const v = attempts[i + 1];
                            return <td key={i} style={{ textAlign: "center", padding: "2px 4px", color: v != null ? "var(--c-text)" : "var(--c-text-dim)" }}>
                              {v != null ? v.toFixed(1) : "-"}
                            </td>;
                          })}
                        </tr>
                      ))}
                      {r.finals && (
                        <tr style={{ borderTop: "1px solid var(--c-border)" }}>
                          <td style={{ padding: "4px 4px 2px", color: "var(--c-text-muted)", fontWeight: 600 }}>Final</td>
                          {Array.from({ length: maxAttempts }, (_, i) => {
                            const v = r.finals?.[i + 1];
                            return <td key={i} style={{ textAlign: "center", padding: "4px 4px 2px", color: v != null ? "var(--c-accent)" : "var(--c-text-dim)", fontWeight: 700 }}>
                              {v != null ? v.toFixed(1) : "-"}
                            </td>;
                          })}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {riders.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-title">No riders in this heat</div>
        </div>
      )}

      {/* ── Edit Heats Modal ── */}
      {editHeatId && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "flex-start",
          paddingTop: 60,
        }} onClick={() => setEditHeatId(null)}>
          <div style={{
            background: "var(--c-bg)", border: "1px solid var(--c-border)",
            padding: 24, width: 420, maxHeight: "70vh", overflow: "auto",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                Edit: {heats.find((h) => h.id === editHeatId)?.name}
              </h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditHeatId(null)}>&times;</button>
            </div>

            {/* Heat selector tabs inside modal */}
            <div className="flex gap-2" style={{ marginBottom: 16, flexWrap: "wrap" }}>
              {heats.map((h) => (
                <button key={h.id}
                  className={`btn btn-sm ${editHeatId === h.id ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setEditHeatId(h.id)}
                >{h.name}</button>
              ))}
            </div>

            {/* Current riders in heat */}
            <div className="field-label" style={{ marginBottom: 8 }}>Riders in heat</div>
            {(allRiders[editHeatId] ?? []).length === 0 ? (
              <div className="text-sm text-muted" style={{ marginBottom: 12 }}>No riders yet</div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                {(allRiders[editHeatId] ?? []).map((r) => (
                  <div key={r.id} className="flex items-center justify-between" style={{ padding: "4px 0", borderBottom: "1px solid var(--c-border)" }}>
                    <span style={{ fontSize: 13 }}>{r.name}</span>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: "#ef4444" }}
                      onClick={() => {
                        setAllRiders((prev) => ({
                          ...prev,
                          [editHeatId]: (prev[editHeatId] ?? []).filter((x) => x.id !== r.id),
                        }));
                        const remaining = (allRiders[editHeatId] ?? []).filter((x) => x.id !== r.id).map((x) => x.id);
                        onRidersChanged?.(editHeatId, remaining);
                      }}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* Available riders to add */}
            <div className="field-label" style={{ marginBottom: 8 }}>Add rider</div>
            {availableRiders
              .filter((ar) => !(allRiders[editHeatId] ?? []).some((r) => r.id === ar.id))
              .map((ar) => (
                <div key={ar.id} className="flex items-center justify-between" style={{ padding: "4px 0" }}>
                  <div>
                    <span className="text-muted" style={{ fontSize: 13 }}>{ar.name}</span>
                    {ar.nickname && <span className="text-muted" style={{ fontSize: 11, marginLeft: 6 }}>{ar.nickname}</span>}
                  </div>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => {
                      const newRider: MockProducerRider = {
                        id: ar.id, name: ar.name, nickname: ar.nickname,
                        attempts: 3, currentAttempt: 0,
                        judgeScores: {}, finals: {},
                      };
                      setAllRiders((prev) => ({
                        ...prev,
                        [editHeatId]: [...(prev[editHeatId] ?? []), newRider],
                      }));
                      const newIds = [...(allRiders[editHeatId] ?? []).map((x) => x.id), ar.id];
                      onRidersChanged?.(editHeatId, newIds);
                    }}>Add</button>
                </div>
              ))}

            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <Button size="sm" onClick={() => setEditHeatId(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
